import React, { useCallback, useMemo, useRef } from 'react';
import { Input, Row, Col, FormGroup, Button, InputGroup, InputGroupAddon } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import {
    TermForm,
    PurchasePriceSummary,
    DeferredFixedPaymentRow,
    DeferredFixedPaymentType,
    DfpCompoundingFrequency,
    PutCallRight,
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS,
    DeferredVariablePaymentRow,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS,
    DeferredVariablePaymentType,
    JvEquityRow,
    JV_EQUITY_COLUMN_LABELS,
    CorporateEquityRow,
    CorporateEquityType,
    CORPORATE_EQUITY_COMMON_COLUMN_LABELS,
    CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS,
    DividendPaymentTiming,
    CompoundingFrequency,
    NO_VALUE,
    YES_VALUE,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { LegalIssueItem } from '../../../../../../slices/marketplace/loi-form/legal-issue-item';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys, LoiTermFormRowFieldKey } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import { TermsFormPriceSummaryBlock } from './TermsFormPriceSummaryBlock';
import { TermsFormDetailBlock } from './TermsFormDetailBlock';
import {
    DFP_COLUMN_DEFS,
    DVP_COLUMN_DEFS,
    JV_COLUMN_DEFS,
    CORP_COMMON_COLUMN_DEFS,
    CORP_PREFERRED_COLUMN_DEFS,
    dfpRowToKeyValue,
    dvpRowToKeyValue,
    jvRowToKeyValue,
    corpCommonRowToKeyValue,
    corpPreferredRowToKeyValue,
    totalDfp,
    totalDvp,
} from './termsFormDetailColumns';
import { TermsFormLeaseTermsBlock } from './TermsFormLeaseTermsBlock';
import { TermsFormGeneralDeferredPaymentsBlock } from './TermsFormGeneralDeferredPaymentsBlock';
import { TermsFormPropertyPurchasedBlock } from './TermsFormPropertyPurchasedBlock';
import './TermsFormDisplay.scss';


export interface TermsFormDisplayProps {
    termForm: TermForm;
    legalIssues?: LegalIssueItem[];
    additionalCovenants?: LegalIssueItem[];
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isFsb?: boolean;
    isSfas?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
    onAdminCommentChange?: (fieldKey: string, adminComment: string | null) => void;
    isAdmin?: boolean;
    canEditFairValues?: boolean;
    isBuyer?: boolean;
}


function summaryTotal(summary: PurchasePriceSummary | null | undefined): number {
    if (!summary) return 0;
    const cash = Number(summary.cashAtCloser?.cashAtClose) || 0;
    const dfp = totalDfp(summary.deferredFixedPayments?.rows ?? []);
    const dvp = totalDvp(summary.deferredVariablePayments?.rows ?? []);
    const jv = Number(summary.jvEquity?.row?.valueAtClosing) || 0;
    const corp = (summary.corporateEquity?.rows ?? []).reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const other = Number(summary.other) || 0;
    return cash + dfp + dvp + jv + corp + other;
}

function rowFromKeyValue(kv: Record<string, string | null>, base?: DeferredFixedPaymentRow): DeferredFixedPaymentRow {
    return {
        considerationForEmployees: base?.considerationForEmployees ?? null,
        buyerConsiderationForEmployees: base?.buyerConsiderationForEmployees ?? null,
        fairValue: base?.fairValue ?? null,
        fairValueExplanation: base?.fairValueExplanation ?? null,
        paymentTiming: kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentTiming] ?? null,
        type: (kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.type] as DeferredFixedPaymentType) ?? null,
        amount:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount] != null &&
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount] !== ''
                ? Number(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount])
                : null,
        annualizedInterestAccrualRate:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate] ?? null,
        compoundingFrequency:
            (kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.compoundingFrequency] as DfpCompoundingFrequency) ?? null,
        contingent: kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent] ?? null,
        contingencyDependentVariableValue:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable] ?? null,
        contDvMinimumValue:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue] != null &&
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue] !== ''
                ? Number(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue])
                : null,
        employmentContingency:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency] ?? null,
        tailPeriodForEmployment:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment] ?? null,
        buyerCalculationPeriodDays: parseDays(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.buyerCalculationPeriod]),
        sellerCalculationResponsePeriodDays: parseDays(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.sellerCalculationResponsePeriod]),
        paymentDateDays: parseDays(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentDate]),
    };
}

function parseDays(value: string | null | undefined): number | null {
    return value != null && value !== '' ? Number(value) : null;
}
function dvpRowFromKeyValue(kv: Record<string, string | null>, base?: DeferredVariablePaymentRow): DeferredVariablePaymentRow {
    const typeVal = kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type];
    let type: DeferredVariablePaymentType | null = null;
    if (typeVal === DeferredVariablePaymentType.Earnout || typeVal === DeferredVariablePaymentType.Synthetic) {
        type = typeVal;
    } else if (typeVal?.toLowerCase().includes('synthetic')) {
        type = DeferredVariablePaymentType.Synthetic;
    } else if (typeVal?.toLowerCase() === 'earnout') {
        type = DeferredVariablePaymentType.Earnout;
    }
    return {
        considerationForEmployees: base?.considerationForEmployees ?? null,
        buyerConsiderationForEmployees: base?.buyerConsiderationForEmployees ?? null,
        fairValue: base?.fairValue ?? null,
        fairValueExplanation: base?.fairValueExplanation ?? null,
        paymentTiming: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentTiming] ?? null,
        type,
        valueAtClosing:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing] != null &&
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing] !== ''
                ? Number(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing])
                : null,
        calculationDependentVariable:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationDependentVariable] ?? null,
        calcDvBaselineValue:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue] != null &&
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue] !== ''
                ? Number(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue])
                : null,
        calculationMultiple:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationMultiple] ?? null,
        maximumPayment: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment] ?? null,
        employmentContingency:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency] ?? null,
        tailPeriodForEmploymentContingency:
            kv[
                DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency
            ] ?? null,
        penaltyMultiple: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.penaltyMultiple] ?? null,
        distributionPeriodicity:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.distributionPeriodicity] ?? null,
        buyerCalculationPeriodDays: parseDays(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.buyerCalculationPeriod]),
        sellerCalculationResponsePeriodDays: parseDays(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.sellerCalculationResponsePeriod]),
        paymentDateDays: parseDays(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentDate]),
        putCallRight: (kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.putCallRight] as PutCallRight) ?? null,
        putCallExplanation: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.putCallExplanation] ?? null,
    };
}

export const TermsFormDisplay = (props: TermsFormDisplayProps) => {
    const { termForm, legalIssues = [], additionalCovenants = [], readOnly = false, onChange, errors, isFsb, isSfas, fieldComments, onFieldCommentChange, onAdminCommentChange, isAdmin, canEditFairValues, isBuyer } = props;
    const isBuyerReview = !isAdmin && !!props.isBuyerReview;
    // Admin sees both comment columns on a completed (read-only) form — never while filling it up
    const isBuyerReviewConfirmed = isAdmin ? readOnly : !!props.isBuyerReviewConfirmed;
    const summary = termForm.purchasePriceSummary;

    const getComment = (fieldKey: string) =>
        fieldComments?.find(c => c.fieldKey === fieldKey)?.buyerComment ?? null;
    const getAdminComment = (fieldKey: string) =>
        fieldComments?.find(c => c.fieldKey === fieldKey)?.adminComment ?? null;

    const showComments = isBuyerReview || isBuyerReviewConfirmed;
    const isAdminCommentEditable = !!isAdmin && (!readOnly || !!canEditFairValues);
    const showAdminComment = showComments || isAdminCommentEditable;

    const commentCell = (fieldKey: string) => {
        if (!showComments) return null;
        const comment = getComment(fieldKey);
        return (
            <td className="terms-form-col-comment">
                <Input
                    type="textarea"
                    value={comment ?? ''}
                    readOnly={!isBuyerReview}
                    onChange={(e) =>
                        onFieldCommentChange?.(fieldKey, e.target.value === '' ? null : e.target.value)
                    }
                    className="terms-form-comment-input"
                    aria-label={`Comment for ${fieldKey}`}
                />
            </td>
        );
    };

    const adminCommentCell = (fieldKey: string) => {
        if (!showAdminComment) return null;
        const adminComment = getAdminComment(fieldKey);
        return (
            <td className="terms-form-col-comment terms-form-col-admin-comment">
                <Input
                    type="textarea"
                    value={adminComment ?? ''}
                    readOnly={!isAdminCommentEditable}
                    onChange={(e) =>
                        onAdminCommentChange?.(fieldKey, e.target.value === '' ? null : e.target.value)
                    }
                    className="terms-form-comment-input"
                    aria-label={`Admin comment for ${fieldKey}`}
                />
            </td>
        );
    };
    const total = summaryTotal(summary);
    const loiDatePickerRef = useRef<any>(null);
    const update = useCallback(
        (patch: Partial<TermForm>) => {
            onChange?.({ ...termForm, ...patch });
        },
        [termForm, onChange]
    );

    const updateSummary = useCallback(
        (patch: Partial<PurchasePriceSummary>) => {
            onChange?.({
                ...termForm,
                purchasePriceSummary: {
                    ...(termForm.purchasePriceSummary ?? {}),
                    ...patch,
                },
            });
        },
        [termForm, onChange]
    );

    const setDfpRows = useCallback(
        (rows: DeferredFixedPaymentRow[]) => {
            updateSummary({ deferredFixedPayments: { rows } });
        },
        [updateSummary]
    );
    const setDvpRows = useCallback(
        (rows: DeferredVariablePaymentRow[]) => {
            updateSummary({ deferredVariablePayments: { rows } });
        },
        [updateSummary]
    );
    const setJvRow = useCallback(
        (row: JvEquityRow | null) => {
            updateSummary({ jvEquity: { row } });
        },
        [updateSummary]
    );
    const corpRows = summary?.corporateEquity?.rows ?? [];

    const setCorpTypedRow = useCallback(
        (type: CorporateEquityType, row: CorporateEquityRow | null) => {
            const idx = corpRows.findIndex((r) => r.type === type);
            const nextRows = row == null
                ? corpRows.filter((r) => r.type !== type)
                : idx >= 0
                    ? corpRows.map((r, i) => (i === idx ? row : r))
                    : [...corpRows, row];
            updateSummary({ corporateEquity: { rows: nextRows } });
        },
        [updateSummary, corpRows]
    );

    const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
    const dvpVisibleIndices = dvpRows
        .map((_, i) => i)
        .filter((i) => !readOnly || (dvpRows[i].valueAtClosing != null && Number(dvpRows[i].valueAtClosing) !== 0));
    const hasDfpValue = dfpRows.length > 0 && totalDfp(dfpRows) > 0;
    const hasDvpValue = dvpRows.length > 0 && totalDvp(dvpRows) > 0;
    const jvRow = summary?.jvEquity?.row ?? null;
    const corpCommonRow = corpRows.find((r) => r.type === CorporateEquityType.Common) ?? null;
    const corpPreferredRow = corpRows.find((r) => r.type === CorporateEquityType.Preferred) ?? null;
    const jvVal = summary?.jvEquity?.row?.valueAtClosing;
    const hasJvValue = jvVal != null && Number(jvVal) !== 0;
    const corpCommonVal = corpCommonRow?.valueAtClosing;
    const hasCorpCommonValue = corpCommonVal != null && Number(corpCommonVal) !== 0;
    const corpPreferredVal = corpPreferredRow?.valueAtClosing;
    const hasCorpPreferredValue = corpPreferredVal != null && Number(corpPreferredVal) !== 0;

    const dfpRowComments = dfpRows.map((_, i) =>
        fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.dfpRow(i))?.buyerComment ?? null
    );
    const handleDfpRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.dfpRow(rowIndex), comment);
    const dfpAdminRowComments = dfpRows.map((_, i) =>
        fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.dfpRow(i))?.adminComment ?? null
    );
    const handleDfpAdminRowCommentChange = (rowIndex: number, adminComment: string | null) =>
        onAdminCommentChange?.(LoiTermFormRowFieldKey.dfpRow(rowIndex), adminComment);

    const jvRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.jvEquityRow(0))?.buyerComment ?? null];
    const handleJvRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.jvEquityRow(rowIndex), comment);
    const jvAdminRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.jvEquityRow(0))?.adminComment ?? null];
    const handleJvAdminRowCommentChange = (rowIndex: number, adminComment: string | null) =>
        onAdminCommentChange?.(LoiTermFormRowFieldKey.jvEquityRow(rowIndex), adminComment);

    const corpCommonRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.corporateEquityRow('Common', 0))?.buyerComment ?? null];
    const handleCorpCommonRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.corporateEquityRow('Common', rowIndex), comment);
    const corpCommonAdminRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.corporateEquityRow('Common', 0))?.adminComment ?? null];
    const handleCorpCommonAdminRowCommentChange = (rowIndex: number, adminComment: string | null) =>
        onAdminCommentChange?.(LoiTermFormRowFieldKey.corporateEquityRow('Common', rowIndex), adminComment);

    const corpPreferredRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.corporateEquityRow('Preferred', 0))?.buyerComment ?? null];
    const handleCorpPreferredRowCommentChange = (rowIndex: number, comment: string | null) =>
        onFieldCommentChange?.(LoiTermFormRowFieldKey.corporateEquityRow('Preferred', rowIndex), comment);
    const corpPreferredAdminRowComments = [fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.corporateEquityRow('Preferred', 0))?.adminComment ?? null];
    const handleCorpPreferredAdminRowCommentChange = (rowIndex: number, adminComment: string | null) =>
        onAdminCommentChange?.(LoiTermFormRowFieldKey.corporateEquityRow('Preferred', rowIndex), adminComment);

    const dfpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('dfp:')) cells.add(key.substring(4));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const dvpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('dvp:')) cells.add(key.substring(4));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const dvpDisplayInvalidCells = (() => {
        if (!dvpInvalidCells) return undefined;
        const remapped = new Set<string>();
        dvpInvalidCells.forEach((cell) => {
            const sep = cell.indexOf(':');
            const displayIndex = dvpVisibleIndices.indexOf(Number(cell.substring(0, sep)));
            if (displayIndex !== -1) remapped.add(`${displayIndex}${cell.substring(sep)}`);
        });
        return remapped.size > 0 ? remapped : undefined;
    })();

    const jvInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('jv:')) cells.add(key.substring(3));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const corpCommonInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        const prefix = 'corp:Common:';
        Array.from(errors).forEach((key) => {
            if (key.startsWith(prefix)) cells.add(key.substring(prefix.length));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const corpPreferredInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        const prefix = 'corp:Preferred:';
        Array.from(errors).forEach((key) => {
            if (key.startsWith(prefix)) cells.add(key.substring(prefix.length));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    return (
        <div className="terms-form-display">
            <div className="terms-form-section">
                <div className="terms-form-section-header">LOI Terms</div>
                <div className="terms-form-section-body">
                    <div className="terms-form-detail-block terms-form-section-underlined">
                        <div className="terms-period-table terms-form-price-summary-table-container">
                            <table className="terms-form-price-summary-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '195px', minWidth: '195px' }}/>
                                        <th className="terms-form-col-value" style={{ width: '155px', minWidth: '155px' }}/>
                                        {showAdminComment && (
                                            <th className="terms-form-col-comment terms-form-col-admin-comment">Admin Comment</th>
                                        )}
                                        {showComments && (
                                            <th className="terms-form-col-comment">Buyer Comment</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="terms-form-col-item table-label-cell">LOI Expiration Date</td>
                                        <td>
                                            {readOnly ? (
                                                <Input
                                                    type="text"
                                                    value={
                                                        termForm.loiExpirationDate != null
                                                            ? termForm.loiExpirationDate.split('T')[0]
                                                            : ''
                                                    }
                                                    disabled
                                                    className="expiration-date-input form-control loi-form-input expiration-date-input"
                                                    aria-label="LOI Expiration Date"
                                                />
                                            ) : (
                                                <div
                                                    className="terms-form-date-picker-wrap"
                                                    onClick={() =>
                                                        loiDatePickerRef.current?.setOpen?.(true)
                                                    }>
                                                    <div className={`d-flex small-border expiration-date-input ${errors?.has('loiExpirationDate') ? ' is-invalid' : ''}`}>
                                                        <InputGroupAddon
                                                            addonType="prepend"
                                                            onClick={() =>
                                                                loiDatePickerRef.current?.setOpen?.(true)
                                                            }>
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon icon={faCalendarAlt} />
                                                            </div>
                                                        </InputGroupAddon>
                                                        <DatePicker
                                                            ref={loiDatePickerRef}
                                                            selected={
                                                                termForm.loiExpirationDate
                                                                    ? new Date(termForm.loiExpirationDate)
                                                                    : null
                                                            }
                                                            onChange={(date: Date | null) =>
                                                                update({
                                                                    loiExpirationDate: date
                                                                        ? date.toISOString().slice(0, 10)
                                                                        : null,
                                                                })
                                                            }
                                                            dateFormat="MM/dd/yyyy"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        {adminCommentCell(LoiTermFormFieldKeys.loiExpirationDate)}
                                        {commentCell(LoiTermFormFieldKeys.loiExpirationDate)}
                                    </tr>
                                    <tr>
                                        <td className="terms-form-col-item table-label-cell">Exclusivity Period</td>
                                        <td className="terms-form-col-value">
                                            <div className="terms-form-col-value-with-unit">
                                                <Input
                                                    type="number"
                                                    value={termForm.exclusivityPeriodDays ?? ''}
                                                    disabled={readOnly}
                                                    onChange={(e) =>
                                                        update({
                                                            exclusivityPeriodDays:
                                                                e.target.value === ''
                                                                    ? null
                                                                    : Number(e.target.value),
                                                        })
                                                    }
                                                    invalid={errors?.has('exclusivityPeriodDays')}
                                                    className="form-control loi-form-input"
                                                    aria-label="Exclusivity Period"
                                                />
                                                <span className="terms-form-unit-label">Days</span>
                                            </div>
                                        </td>
                                        {adminCommentCell(LoiTermFormFieldKeys.exclusivityPeriodDays)}
                                        {commentCell(LoiTermFormFieldKeys.exclusivityPeriodDays)}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <TermsFormPropertyPurchasedBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        fieldComments={fieldComments}
                        onFieldCommentChange={onFieldCommentChange}
                        onAdminCommentChange={onAdminCommentChange}
                        isAdmin={isAdmin}
                    />

                    <TermsFormPriceSummaryBlock
                        isSfas={isSfas}
                        summary={summary}
                        total={total}
                        onSummaryChange={updateSummary}
                        readOnly={readOnly}
                        errors={errors}
                        employeeConsiderationPayer={termForm.employeeConsiderationPayer}
                        isFsb={isFsb}
                        isBuyer={isBuyer}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        fieldComments={fieldComments}
                        onFieldCommentChange={onFieldCommentChange}
                        onAdminCommentChange={onAdminCommentChange}
                        isAdmin={isAdmin}
                        canEditFairValues={canEditFairValues}
                    />

                    {(hasDfpValue || hasDvpValue) && (
                        <TermsFormGeneralDeferredPaymentsBlock
                            termForm={termForm}
                            readOnly={readOnly}
                            onChange={onChange}
                            errors={errors}
                            isBuyerReview={isBuyerReview}
                            isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            fieldComments={fieldComments}
                            onFieldCommentChange={onFieldCommentChange}
                            onAdminCommentChange={onAdminCommentChange}
                            isAdmin={isAdmin}
                        />
                    )}

                    {hasDfpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--deferred-fixed-payments"
                            hideLastRowBorder
                            title="Deferred Fixed Payments"
                            columns={DFP_COLUMN_DEFS}
                            rows={dfpRows.map(dfpRowToKeyValue)}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setDfpRows(rowsKv.map((kv, i) => rowFromKeyValue(kv, dfpRows[i])))
                            }
                            displayTotal={totalDfp(dfpRows)}
                            showTotalRow
                            onAddRow={() =>
                                setDfpRows([
                                    ...dfpRows,
                                    {
                                        paymentTiming: null,
                                        type: null,
                                        amount: null,
                                        annualizedInterestAccrualRate: null,
                                        compoundingFrequency: null,
                                        contingent: NO_VALUE,
                                        contingencyDependentVariableValue: null,
                                        contDvMinimumValue: null,
                                        employmentContingency: NO_VALUE,
                                        tailPeriodForEmployment: null,
                                        buyerCalculationPeriodDays: null,
                                        sellerCalculationResponsePeriodDays: null,
                                        paymentDateDays: null,
                                    },
                                ])
                            }
                            onRemoveRow={(i) => setDfpRows(dfpRows.filter((_, idx) => idx !== i))}
                            cellHidingRules={[
                                {
                                    column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable,
                                    dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue,
                                    dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment,
                                    dependsOnColumn: DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency,
                                    visibleWhen: YES_VALUE,
                                },
                            ]}
                            invalidCells={dfpInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={dfpRowComments}
                            onRowCommentChange={handleDfpRowCommentChange}
                            isAdmin={isAdmin}
                            adminRowComments={dfpAdminRowComments}
                            onAdminRowCommentChange={handleDfpAdminRowCommentChange}
                        />
                    )}

                    {hasDvpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--deferred-variable-payments"
                            hideLastRowBorder
                            title="Deferred Variable Payments"
                            columns={DVP_COLUMN_DEFS}
                            rows={dvpVisibleIndices.map((i) => dvpRowToKeyValue(dvpRows[i]))}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setDvpRows(rowsKv.map((kv, i) => dvpRowFromKeyValue(kv, dvpRows[i])))
                            }
                            onAddRow={() =>
                                setDvpRows([
                                    ...dvpRows,
                                    {
                                        paymentTiming: null,
                                        type: null,
                                        valueAtClosing: null,
                                        calculationDependentVariable: null,
                                        calcDvBaselineValue: null,
                                        calculationMultiple: null,
                                        maximumPayment: null,
                                        employmentContingency: NO_VALUE,
                                        tailPeriodForEmploymentContingency: null,
                                        penaltyMultiple: null,
                                        distributionPeriodicity: null,
                                        buyerCalculationPeriodDays: null,
                                        sellerCalculationResponsePeriodDays: null,
                                        paymentDateDays: null,
                                        putCallRight: PutCallRight.NA,
                                        putCallExplanation: null,
                                    },
                                ])
                            }
                            onRemoveRow={(i) => setDvpRows(dvpRows.filter((_, idx) => idx !== i))}
                            showPaymentNumber
                            paymentNumberHeader=""
                            cellHidingRules={[
                                {
                                    column: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency,
                                    dependsOnColumn: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.putCallExplanation,
                                    dependsOnColumn: DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                            ]}
                            invalidCells={dvpDisplayInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={dvpVisibleIndices.map((i) =>
                                fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.dvpRow(i))?.buyerComment ?? null
                            )}
                            onRowCommentChange={(i, c) => onFieldCommentChange?.(LoiTermFormRowFieldKey.dvpRow(dvpVisibleIndices[i]), c)}
                            isAdmin={isAdmin}
                            adminRowComments={dvpVisibleIndices.map((i) =>
                                fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.dvpRow(i))?.adminComment ?? null
                            )}
                            onAdminRowCommentChange={(i, c) => onAdminCommentChange?.(LoiTermFormRowFieldKey.dvpRow(dvpVisibleIndices[i]), c)}
                        />
                    )}

                    {hasJvValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--jv-equity"
                            title="JV Equity"
                            columns={JV_COLUMN_DEFS}
                            rows={[jvRowToKeyValue(jvRow)]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setJvRow(
                                    rowsKv.length > 0
                                        ? {
                                              considerationForEmployees: jvRow?.considerationForEmployees ?? null,
                                              buyerConsiderationForEmployees: jvRow?.buyerConsiderationForEmployees ?? null,
                                              fairValue: jvRow?.fairValue ?? null,
                                              fairValueExplanation: jvRow?.fairValueExplanation ?? null,
                                              valueAtClosing:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing] != null &&
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing])
                                                      : null,
                                              paymentTiming:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.paymentTiming] ?? null,
                                              calculationDependentVariable:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calculationDependentVariable] ??
                                                  null,
                                              calcDvBaselineValue:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue] != null &&
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue] !== ''
                                                      ? Number(rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue])
                                                      : null,
                                              calculationMultiple:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calculationMultiple] ?? null,
                                              employmentContingency:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.employmentContingency] ?? null,
                                              tailPeriodForEmploymentContingency:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency] ??
                                                  null,
                                              penaltyMultiple:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.penaltyMultiple] ?? null,
                                              distributionPeriodicity:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.distributionPeriodicity] ?? null,
                                              buyerCalculationPeriodDays: parseDays(rowsKv[0][JV_EQUITY_COLUMN_LABELS.buyerCalculationPeriod]),
                                              sellerCalculationResponsePeriodDays: parseDays(rowsKv[0][JV_EQUITY_COLUMN_LABELS.sellerCalculationResponsePeriod]),
                                              paymentDateDays: parseDays(rowsKv[0][JV_EQUITY_COLUMN_LABELS.paymentDate]),
                                              putCallRight: (rowsKv[0][JV_EQUITY_COLUMN_LABELS.putCallRight] as PutCallRight) ?? null,
                                              putCallExplanation: rowsKv[0][JV_EQUITY_COLUMN_LABELS.putCallExplanation] ?? null,
                                            }
                                        : null
                                )
                            }
                            singleRow
                            hideAddRow
                            showRowBorder={false}
                            cellHidingRules={[
                                {
                                    column: JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency,
                                    dependsOnColumn: JV_EQUITY_COLUMN_LABELS.employmentContingency,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: JV_EQUITY_COLUMN_LABELS.putCallExplanation,
                                    dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: JV_EQUITY_COLUMN_LABELS.buyerCalculationPeriod,
                                    dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: JV_EQUITY_COLUMN_LABELS.sellerCalculationResponsePeriod,
                                    dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: JV_EQUITY_COLUMN_LABELS.paymentDate,
                                    dependsOnColumn: JV_EQUITY_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                            ]}
                            invalidCells={jvInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={jvRowComments}
                            onRowCommentChange={handleJvRowCommentChange}
                            isAdmin={isAdmin}
                            adminRowComments={jvAdminRowComments}
                            onAdminRowCommentChange={handleJvAdminRowCommentChange}
                        />
                    )}

                    {hasCorpCommonValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--corporate-equity-common"
                            title="Common Corp Equity"
                            columns={CORP_COMMON_COLUMN_DEFS}
                            rows={[corpCommonRowToKeyValue(corpCommonRow)]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setCorpTypedRow(
                                    CorporateEquityType.Common,
                                    rowsKv.length > 0
                                        ? {
                                              type: CorporateEquityType.Common,
                                              considerationForEmployees: corpCommonRow?.considerationForEmployees ?? null,
                                              buyerConsiderationForEmployees: corpCommonRow?.buyerConsiderationForEmployees ?? null,
                                              fairValue: corpCommonRow?.fairValue ?? null,
                                              fairValueExplanation: corpCommonRow?.fairValueExplanation ?? null,
                                              valueAtClosing:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.valueAtClosing] != null &&
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.valueAtClosing])
                                                      : null,
                                              expectedTimingToRecap:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.expectedTimingToRecap] ?? null,
                                              expectedPayoutAsMultipleOfCurrentValue:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.expectedPayoutAsMultipleOfCurrentValue] ?? null,
                                              contingent:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingent] ?? null,
                                              contingencyDependentVariableValue:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingencyDependentVariable] ?? null,
                                              contDvMinimumValue:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contDvMinimumValue] != null &&
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contDvMinimumValue] !== ''
                                                      ? Number(rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contDvMinimumValue])
                                                      : null,
                                              employmentContingency:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.employmentContingency] ?? null,
                                              tailPeriodForEmploymentContingency:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.tailPeriodForEmploymentContingency] ?? null,
                                              penaltyMultiple:
                                                  rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.penaltyMultiple] ?? null,
                                              buyerCalculationPeriodDays: parseDays(rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.buyerCalculationPeriod]),
                                              sellerCalculationResponsePeriodDays: parseDays(rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.sellerCalculationResponsePeriod]),
                                              paymentDateDays: parseDays(rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.paymentDate]),
                                              putCallRight: (rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight] as PutCallRight) ?? null,
                                              putCallExplanation: rowsKv[0][CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallExplanation] ?? null,
                                          }
                                        : null
                                )
                            }
                            singleRow
                            showRowBorder={false}
                            cellHidingRules={[
                                {
                                    column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingencyDependentVariable,
                                    dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingent,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contDvMinimumValue,
                                    dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.contingent,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.tailPeriodForEmploymentContingency,
                                    dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.employmentContingency,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallExplanation,
                                    dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.buyerCalculationPeriod,
                                    dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.sellerCalculationResponsePeriod,
                                    dependsOnColumn: CORPORATE_EQUITY_COMMON_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                            ]}
                            invalidCells={corpCommonInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={corpCommonRowComments}
                            onRowCommentChange={handleCorpCommonRowCommentChange}
                            isAdmin={isAdmin}
                            adminRowComments={corpCommonAdminRowComments}
                            onAdminRowCommentChange={handleCorpCommonAdminRowCommentChange}
                        />
                    )}

                    {hasCorpPreferredValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--corporate-equity-preferred"
                            title="Preferred Corp Equity"
                            columns={CORP_PREFERRED_COLUMN_DEFS}
                            rows={[corpPreferredRowToKeyValue(corpPreferredRow)]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) => {
                                const L = CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS;
                                setCorpTypedRow(
                                    CorporateEquityType.Preferred,
                                    rowsKv.length > 0
                                        ? {
                                              type: CorporateEquityType.Preferred,
                                              considerationForEmployees: corpPreferredRow?.considerationForEmployees ?? null,
                                              buyerConsiderationForEmployees: corpPreferredRow?.buyerConsiderationForEmployees ?? null,
                                              fairValue: corpPreferredRow?.fairValue ?? null,
                                              fairValueExplanation: corpPreferredRow?.fairValueExplanation ?? null,
                                              valueAtClosing:
                                                  rowsKv[0][L.valueAtClosing] != null && rowsKv[0][L.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][L.valueAtClosing])
                                                      : null,
                                              annualDividendRate: rowsKv[0][L.annualDividendRate] ?? null,
                                              dividendPaymentTiming:
                                                  (rowsKv[0][L.dividendPaymentTiming] as DividendPaymentTiming) || null,
                                              compoundingFrequency:
                                                  (rowsKv[0][L.compoundingFrequency] as CompoundingFrequency) || null,
                                              expectedTimingToRecap: rowsKv[0][L.expectedTimingToRecap] ?? null,
                                              convertibleIntoCommon: rowsKv[0][L.convertibleIntoCommon] === YES_VALUE,
                                              conversionRatio:
                                                  rowsKv[0][L.conversionRatio] != null && rowsKv[0][L.conversionRatio] !== ''
                                                      ? Number(rowsKv[0][L.conversionRatio])
                                                      : null,
                                              contingent: rowsKv[0][L.contingent] ?? null,
                                              contingencyDependentVariableValue:
                                                  rowsKv[0][L.contingencyDependentVariable] ?? null,
                                              contDvMinimumValue:
                                                  rowsKv[0][L.contDvMinimumValue] != null && rowsKv[0][L.contDvMinimumValue] !== ''
                                                      ? Number(rowsKv[0][L.contDvMinimumValue])
                                                      : null,
                                              employmentContingency: rowsKv[0][L.employmentContingency] ?? null,
                                              tailPeriodForEmploymentContingency:
                                                  rowsKv[0][L.tailPeriodForEmploymentContingency] ?? null,
                                              penaltyMultiple: rowsKv[0][L.penaltyMultiple] ?? null,
                                              buyerCalculationPeriodDays: parseDays(rowsKv[0][L.buyerCalculationPeriod]),
                                              sellerCalculationResponsePeriodDays: parseDays(rowsKv[0][L.sellerCalculationResponsePeriod]),
                                              paymentDateDays: parseDays(rowsKv[0][L.paymentDate]),
                                              putCallRight: (rowsKv[0][L.putCallRight] as PutCallRight) ?? null,
                                              putCallExplanation: rowsKv[0][L.putCallExplanation] ?? null,
                                          }
                                        : null
                                );
                            }}
                            singleRow
                            showRowBorder={false}
                            cellHidingRules={[
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.compoundingFrequency,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.dividendPaymentTiming,
                                    visibleWhen: DividendPaymentTiming.Cumulative,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.conversionRatio,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.convertibleIntoCommon,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contingencyDependentVariable,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contingent,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contDvMinimumValue,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.contingent,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.tailPeriodForEmploymentContingency,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.employmentContingency,
                                    visibleWhen: YES_VALUE,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallExplanation,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.buyerCalculationPeriod,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                                {
                                    column: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.sellerCalculationResponsePeriod,
                                    dependsOnColumn: CORPORATE_EQUITY_PREFERRED_COLUMN_LABELS.putCallRight,
                                    hiddenWhen: PutCallRight.NA,
                                },
                            ]}
                            invalidCells={corpPreferredInvalidCells}
                            isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                            rowComments={corpPreferredRowComments}
                            onRowCommentChange={handleCorpPreferredRowCommentChange}
                            isAdmin={isAdmin}
                            adminRowComments={corpPreferredAdminRowComments}
                            onAdminRowCommentChange={handleCorpPreferredAdminRowCommentChange}
                        />
                    )}

                    <TermsFormLeaseTermsBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                        isBuyerReview={isBuyerReview}
                        isBuyerReviewConfirmed={isBuyerReviewConfirmed}
                        rowComments={
                            (termForm.locations ?? [])
                                .filter((l) => l.leaseAssumption === 'new')
                                .map((l) =>
                                    fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.leaseRow(l.locationId))?.buyerComment ?? null
                                )
                        }
                        onRowCommentChange={(i, c) => {
                            const locationId = (termForm.locations ?? []).filter(l => l.leaseAssumption === 'new')[i]?.locationId;
                            if (locationId) onFieldCommentChange?.(LoiTermFormRowFieldKey.leaseRow(locationId), c);
                        }}
                        isAdmin={isAdmin}
                        adminRowComments={
                            (termForm.locations ?? [])
                                .filter((l) => l.leaseAssumption === 'new')
                                .map((l) =>
                                    fieldComments?.find(c => c.fieldKey === LoiTermFormRowFieldKey.leaseRow(l.locationId))?.adminComment ?? null
                                )
                        }
                        onAdminRowCommentChange={(i, c) => {
                            const locationId = (termForm.locations ?? []).filter(l => l.leaseAssumption === 'new')[i]?.locationId;
                            if (locationId) onAdminCommentChange?.(LoiTermFormRowFieldKey.leaseRow(locationId), c);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
